import React from 'react';
import { cn } from '@fabric-ui/utils';
import { Box, Stack, Typography, TypographyProps } from '@mui/material';
import { useChartAccessorsContext } from '../../core/ChartRoot';
import { formatPercentage } from '../../utils/formatters';
import type { ArcAngles } from './PieSlice.utils';
import {
  computePolylineLabelPosition,
  computePolylinePoints,
  LINE_HORIZONTAL_LENGTH,
  POLYLINE_LABEL_ROW_GAP,
  POLYLINE_LABEL_ROW_HEIGHT,
  POLYLINE_LABEL_WIDTH,
} from './polyline.utils';

export const polylineLabelClassNames = {
  line: 'Chart-PiePolyline',
  label: 'Chart-PiePolylineLabel',
  labelTop: 'Chart-PiePolylineLabel--Top',
  labelBottom: 'Chart-PiePolylineLabel--Bottom',
  labelRight: 'Chart-PiePolylineLabel--Right',
  labelLeft: 'Chart-PiePolylineLabel--Left',
} as const;

type PolylineLabelProps<Datum> = {
  datum: Datum;
  angles: ArcAngles;
  ringInnerRadius: number;
  ringOuterRadius: number;
  highlighted: boolean;
  selected: boolean;
  color: string;
  total?: number;
  scale?: number;
};

const PolylineTopLabel = ({ sx, ...props }: TypographyProps<'span'>) => (
  <Typography
    variant="caption"
    component="span"
    fontSize={12}
    noWrap
    display="block"
    sx={{
      color: 'var(--color-content-base-secondary)',
      width: 'var(--foreign-object-width)',
      maxWidth: 'var(--foreign-object-width)',
      [`.${polylineLabelClassNames.labelRight} &`]: { textAlign: 'end' },
      [`.${polylineLabelClassNames.labelLeft} &`]: { textAlign: 'start' },
      ...sx,
    }}
    {...props}
  />
);

export const PolylineLabel = <Datum,>({
  datum,
  angles,
  ringInnerRadius,
  ringOuterRadius,
  highlighted,
  selected,
  color,
  total,
  scale = 1,
}: PolylineLabelProps<Datum>) => {
  const { labelFormatter, valueFormatter, getValue } = useChartAccessorsContext<Datum>();

  const dimLabel = labelFormatter?.(datum, { where: 'polyline-label' });
  if (!dimLabel) return null;

  const labelRadius = highlighted ? ringOuterRadius : ringInnerRadius;
  const {
    labelX,
    labelY,
    offsetX,
    showLabel: meetsAngleThreshold,
  } = computePolylineLabelPosition(angles, labelRadius, selected, highlighted, scale);

  if (!meetsAngleThreshold) return null;

  const { startX, startY, elbowX, elbowY } = computePolylinePoints(angles, ringInnerRadius, labelRadius, scale);

  const value = getValue(datum);
  const formattedValue = valueFormatter?.(datum, { where: 'polyline-label' });
  const showValue = formattedValue !== undefined && formattedValue !== '';
  const share = total ? formatPercentage.format(value / total) : undefined;
  const showValueRow = showValue || share;
  const isRight = offsetX >= 0;
  const scaledLabelWidth = POLYLINE_LABEL_WIDTH * scale;
  const lineEndX = isRight ? labelX + scaledLabelWidth : labelX - scaledLabelWidth;
  const foreignObjectWidth = (LINE_HORIZONTAL_LENGTH + POLYLINE_LABEL_WIDTH) * scale;
  const foreignObjectX = isRight ? lineEndX - foreignObjectWidth : lineEndX;

  return (
    <>
      <path
        aria-hidden="true"
        className={polylineLabelClassNames.line}
        d={`M ${startX} ${startY} L ${elbowX} ${elbowY} L ${lineEndX} ${labelY}`}
        stroke={color}
        strokeWidth={1.5}
        fill="none"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <foreignObject
        aria-hidden="true"
        className={cn(
          polylineLabelClassNames.label,
          polylineLabelClassNames.labelTop,
          isRight ? polylineLabelClassNames.labelRight : polylineLabelClassNames.labelLeft
        )}
        x={foreignObjectX}
        y={labelY - POLYLINE_LABEL_ROW_HEIGHT - POLYLINE_LABEL_ROW_GAP}
        width={foreignObjectWidth}
        height={POLYLINE_LABEL_ROW_HEIGHT}
        style={{
          overflow: 'visible',
          pointerEvents: 'none',
          // @ts-expect-error: css var
          '--foreign-object-width': `${foreignObjectWidth}px`,
        }}>
        {React.isValidElement(dimLabel) ? dimLabel : <PolylineTopLabel>{dimLabel}</PolylineTopLabel>}
      </foreignObject>
      {showValueRow && (
        <foreignObject
          aria-hidden="true"
          className={cn(
            polylineLabelClassNames.label,
            polylineLabelClassNames.labelBottom,
            isRight ? polylineLabelClassNames.labelRight : polylineLabelClassNames.labelLeft
          )}
          x={foreignObjectX}
          y={labelY + POLYLINE_LABEL_ROW_GAP}
          width={foreignObjectWidth}
          height={POLYLINE_LABEL_ROW_HEIGHT}
          style={{ overflow: 'visible', pointerEvents: 'none' }}>
          <Stack
            direction={isRight ? 'row-reverse' : 'row'}
            alignItems="center"
            gap={0.75}
            fontSize={12}
            color="var(--color-content-base-primary)">
            {showValue && (
              <Box component="span" sx={{ fontWeight: 600 }}>
                {String(formattedValue)}
              </Box>
            )}
            {showValue && share && (
              <Box
                component="span"
                sx={{
                  width: 1.5,
                  height: 13,
                  backgroundColor: 'var(--color-border-base-secondary)',
                  flexShrink: 0,
                }}
              />
            )}
            {share && <span>{share}</span>}
          </Stack>
        </foreignObject>
      )}
    </>
  );
};
